<template>
  <div v-click-outside="closePanel" :class="{ 'app-date-picker--disabled': disabled }" class="app-date-picker">
    <label v-if="label" class="app-date-picker__label">
      {{ label }}
      <span v-if="required" class="app-date-picker__required">*</span>
      <i v-if="tooltip" :uk-tooltip="'title: ' + tooltip" class="uil-info-circle app-date-picker__tooltip-icon"></i>
    </label>

    <!-- Clickable Input -->
    <div :class="{ 'app-date-picker__input--active': panelOpen }" class="app-date-picker__input" @click="togglePanel">
      <i class="uil-calendar-alt app-date-picker__icon"></i>
      <template v-if="mode === 'range'">
        <span v-if="startDate || endDate" class="app-date-picker__value">
          {{ fmtDisplay(startDate) }}
          <i class="uil-arrow-right app-date-picker__arrow"></i>
          {{ fmtDisplay(endDate) }}
        </span>
        <span v-else class="app-date-picker__placeholder">{{ computedPlaceholder }}</span>
      </template>
      <template v-else>
        <span v-if="localValue" class="app-date-picker__value">{{ fmtDisplay(localValue) }}</span>
        <span v-else class="app-date-picker__placeholder">{{ computedPlaceholder }}</span>
      </template>
      <button v-if="clearable && hasValue && !disabled" class="app-date-picker__clear" type="button"
              @click.stop="clearValue">
        <i class="uil-times"></i>
      </button>
    </div>

    <!-- Panel -->
    <transition name="app-date-picker-fade">
      <div v-if="panelOpen"
           :class="panelPosition === 'bottom' ? 'app-date-picker__panel--bottom' : 'app-date-picker__panel--top'"
           class="app-date-picker__panel"
      >
        <!-- Presets Sidebar -->
        <div v-if="showPresets" class="adp-sidebar">
          <div class="adp-sidebar__title">{{ tSafe('general.quick_select', 'Hızlı Seçim') }}</div>
          <button v-for="p in visiblePresets" :key="p.key"
                  :class="{ 'adp-sidebar__btn--active': activePreset === p.key }"
                  class="adp-sidebar__btn" type="button" @click="applyPreset(p)">
            {{ p.label }}
          </button>
        </div>

        <!-- Calendars -->
        <div class="adp-body">
          <!-- Range summary -->
          <div v-if="mode === 'range' && (startDate || endDate)" class="adp-range-bar">
            <div class="adp-range-bar__item">
              <span class="adp-range-bar__label">{{ computedStartLabel }}</span>
              <span class="adp-range-bar__val">{{ fmtDisplay(startDate) }}</span>
            </div>
            <i class="uil-arrow-right adp-range-bar__arrow"></i>
            <div class="adp-range-bar__item">
              <span class="adp-range-bar__label">{{ computedEndLabel }}</span>
              <span class="adp-range-bar__val">{{ fmtDisplay(endDate) }}</span>
            </div>
          </div>

          <div class="adp-calendars">
            <!-- Left Calendar -->
            <div class="adp-cal">
              <div class="adp-cal__header">
                <button class="adp-cal__nav" type="button" @click="changeMonth(-1)">
                  <i class="uil-angle-left"></i>
                </button>
                <span class="adp-cal__month">{{ monthName(viewMonth) }} {{ viewYear }}</span>
                <span class="adp-cal__nav adp-cal__nav--hidden"></span>
              </div>
              <div class="adp-cal__weekdays">
                <span v-for="d in weekDays" :key="'l'+d" class="adp-cal__wd">{{ d }}</span>
              </div>
              <div class="adp-cal__days">
                <button v-for="(day, i) in leftDays" :key="'ld'+i"
                        :class="dayClasses(day)" :disabled="day.disabled || day.empty"
                        class="adp-day" type="button"
                        @click="onDayClick(day)" @mouseenter="onDayHover(day)" @mouseleave="hoverDate = null">
                  <span v-if="!day.empty" class="adp-day__text">{{ day.day }}</span>
                </button>
              </div>
            </div>

            <!-- Right Calendar -->
            <div class="adp-cal">
              <div class="adp-cal__header">
                <span class="adp-cal__nav adp-cal__nav--hidden"></span>
                <span class="adp-cal__month">{{
                    monthName(viewMonth + 1)
                  }} {{ viewMonth + 1 > 11 ? viewYear + 1 : viewYear }}</span>
                <button class="adp-cal__nav" type="button" @click="changeMonth(1)">
                  <i class="uil-angle-right"></i>
                </button>
              </div>
              <div class="adp-cal__weekdays">
                <span v-for="d in weekDays" :key="'r'+d" class="adp-cal__wd">{{ d }}</span>
              </div>
              <div class="adp-cal__days">
                <button v-for="(day, i) in rightDays" :key="'rd'+i"
                        :class="dayClasses(day)" :disabled="day.disabled || day.empty"
                        class="adp-day" type="button"
                        @click="onDayClick(day)" @mouseenter="onDayHover(day)" @mouseleave="hoverDate = null">
                  <span v-if="!day.empty" class="adp-day__text">{{ day.day }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <small v-if="helperText" class="app-date-picker__helper">{{ helperText }}</small>
  </div>
</template>

<script>
export default {
  name: "AppDatePicker",
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!(el === e.target || el.contains(e.target))) binding.value(e);
        };
        document.addEventListener('click', el._clickOutside, true);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside, true);
        delete el._clickOutside;
      },
    },
  },
  props: {
    value: { type: [String, Object], default: null },
    mode: { type: String, default: "single", validator: (v) => ["single", "range"].includes(v) },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    startLabel: { type: String, default: "" },
    endLabel: { type: String, default: "" },
    tooltip: { type: String, default: "" },
    helperText: { type: String, default: "" },
    format: { type: String, default: "" },
    showTime: { type: Boolean, default: true },
    onlyTime: { type: Boolean, default: false },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    autoClose: { type: Boolean, default: false },
    showPresets: { type: Boolean, default: false },
    presets: { type: Array, default: () => [] },
    panelPosition: {
      type: String,
      default: "top",
      validator: (v) => ["top", "bottom"].includes(v),
    },
  },
  data() {
    const now = new Date();
    const start = this.value && typeof this.value === "object" ? this.value.start || null : null;
    const end = this.value && typeof this.value === "object" ? this.value.end || null : null;
    // If start date exists, show that month; otherwise show current month
    const initDate = start ? new Date(start.split(/[\sT]/)[0] + "T00:00:00") : now;
    return {
      panelOpen: false,
      localValue: this.value && typeof this.value === "string" ? this.value : null,
      startDate: start,
      endDate: end,
      // Selection phase: 'start' = waiting for first click, 'end' = waiting for second click
      selectPhase: "start",
      hoverDate: null,
      viewYear: initDate.getFullYear(),
      viewMonth: initDate.getMonth(),
      activePreset: null,
    };
  },
  computed: {
    computedPlaceholder() {
      if (this.placeholder) return this.placeholder;
      if (this.mode === "range") return this.tSafe("general.select_date_range", "Tarih aralığı seçin");
      return this.tSafe("dateTimePicker.placeholderDate", "Tarih seçin");
    },
    computedStartLabel() {
      return this.startLabel || this.tSafe("general.start_date", "Başlangıç");
    },
    computedEndLabel() {
      return this.endLabel || this.tSafe("general.end_date", "Bitiş");
    },
    computedFormat() {
      if (this.format) return this.format;
      if (this.onlyTime) return "HH:mm:ss";
      return this.showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
    },
    hasValue() {
      if (this.mode === "range") return !!(this.startDate || this.endDate);
      return !!this.localValue;
    },
    weekDays() {
      const locale = this.$i18n ? this.$i18n.locale : "tr";
      const days = [];
      // Start from Monday
      for (let i = 0; i < 7; i++) {
        const d = new Date(2024, 0, i + 1); // 2024-01-01 is Monday
        days.push(d.toLocaleDateString(locale, { weekday: "short" }).slice(0, 2));
      }
      return days;
    },
    leftDays() {
      return this.buildDays(this.viewYear, this.viewMonth);
    },
    rightDays() {
      let m = this.viewMonth + 1;
      let y = this.viewYear;
      if (m > 11) {
        m = 0;
        y++;
      }
      return this.buildDays(y, m);
    },
    todayTs() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    },
    visiblePresets() {
      if (this.presets.length > 0) return this.presets;
      return this.defaultPresets;
    },
    defaultPresets() {
      const today = new Date();
      const fmt = (d) => this.toDateStr(d);
      const daysAgo = (n) => {
        const d = new Date(today);
        d.setDate(d.getDate() - n);
        return d;
      };
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

      if (this.mode === "range") {
        return [
          { key: "today", label: this.tSafe("general.today", "Bugün"), start: fmt(today), end: fmt(today) },
          {
            key: "last7",
            label: this.tSafe("general.last_7_days", "Son 7 gün"),
            start: fmt(daysAgo(7)),
            end: fmt(today)
          },
          {
            key: "last30",
            label: this.tSafe("general.last_30_days", "Son 30 gün"),
            start: fmt(daysAgo(30)),
            end: fmt(today)
          },
          {
            key: "thisMonth",
            label: this.tSafe("general.this_month", "Bu ay"),
            start: fmt(startOfMonth),
            end: fmt(today)
          },
          {
            key: "lastMonth",
            label: this.tSafe("general.last_month", "Geçen ay"),
            start: fmt(startOfLastMonth),
            end: fmt(endOfLastMonth)
          },
        ];
      }
      return [{ key: "today", label: this.tSafe("general.today", "Bugün"), start: fmt(today), end: null }];
    },
  },
  watch: {
    value: {
      handler(v) {
        if (this.mode === "range") {
          if (v && typeof v === "object") {
            this.startDate = v.start || null;
            this.endDate = v.end || null;
          } else {
            this.startDate = null;
            this.endDate = null;
          }
        } else {
          this.localValue = v;
        }
      },
      deep: true,
    },
  },
  methods: {
    tSafe(key, fb) {
      try {
        const v = this.$t(key);
        return v !== key ? v : fb;
      } catch {
        return fb;
      }
    },
    toDateStr(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    parseDate(s) {
      if (!s) return null;
      return new Date(s.split(/[\sT]/)[0] + "T00:00:00");
    },
    fmtDisplay(s) {
      if (!s) return "—";
      const p = s.split(/[\sT]/)[0].split("-");
      if (p.length === 3) return `${p[2]}.${p[1]}.${p[0]}`;
      return s;
    },
    monthName(m) {
      let month = m;
      if (month > 11) month -= 12;
      if (month < 0) month += 12;
      const locale = this.$i18n ? this.$i18n.locale : "tr";
      return new Date(2024, month, 1).toLocaleDateString(locale, { month: "long" });
    },
    togglePanel() {
      if (this.disabled) return;
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) {
        this.selectPhase = "start";
        this.hoverDate = null;
        // Navigate to start date month if set
        if (this.startDate) {
          const d = this.parseDate(this.startDate);
          if (d) {
            this.viewYear = d.getFullYear();
            this.viewMonth = d.getMonth();
          }
        }
      }
    },
    closePanel() {
      this.panelOpen = false;
    },
    changeMonth(dir) {
      this.viewMonth += dir;
      if (this.viewMonth > 11) {
        this.viewMonth = 0;
        this.viewYear++;
      }
      if (this.viewMonth < 0) {
        this.viewMonth = 11;
        this.viewYear--;
      }
    },
    buildDays(year, month) {
      const days = [];
      const firstDay = new Date(year, month, 1).getDay();
      // Convert Sunday=0 to Monday-based (Mon=0)
      const offset = (firstDay + 6) % 7;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const minD = this.minDate ? this.parseDate(this.minDate) : null;
      const maxD = this.maxDate ? this.parseDate(this.maxDate) : null;

      // Empty cells before first day
      for (let i = 0; i < offset; i++) {
        days.push({ empty: true, disabled: true });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        let dis = false;
        if (minD && date < minD) dis = true;
        if (maxD && date > maxD) dis = true;
        days.push({
          day: d,
          date: this.toDateStr(date),
          ts: date.getTime(),
          disabled: dis,
          empty: false,
        });
      }
      return days;
    },
    onDayClick(day) {
      if (day.empty || day.disabled) return;
      if (this.mode === "single") {
        this.localValue = day.date;
        this.$emit("input", day.date);
        if (this.autoClose) this.closePanel();
        return;
      }
      // Range mode
      if (this.selectPhase === "start") {
        // Tek gün seçiminde start ve end aynı güne atanır
        this.startDate = day.date;
        this.endDate = day.date;
        this.hoverDate = null;
        this.selectPhase = "end";
        this.activePreset = null;
        this.$emit("input", { start: this.startDate, end: this.endDate });
      } else {
        const startTs = this.parseDate(this.startDate).getTime();
        if (day.ts < startTs) {
          // Clicked before start → make this the new start (tek gün)
          this.startDate = day.date;
          this.endDate = day.date;
          this.hoverDate = null;
          this.selectPhase = "end";
          this.$emit("input", { start: this.startDate, end: this.endDate });
        } else {
          this.endDate = day.date;
          this.hoverDate = null;
          this.selectPhase = "start";
          this.$emit("input", { start: this.startDate, end: this.endDate });
        }
        this.activePreset = null;
      }
    },
    onDayHover(day) {
      if (day.empty || day.disabled) return;
      if (this.mode === "range" && this.selectPhase === "end" && this.startDate) {
        this.hoverDate = day.ts;
      }
    },
    dayClasses(day) {
      if (day.empty) return ["adp-day--empty"];
      const cls = [];
      const ts = day.ts;
      const startTs = this.startDate ? this.parseDate(this.startDate).getTime() : null;
      const endTs = this.endDate ? this.parseDate(this.endDate).getTime() : null;

      if (ts === this.todayTs) cls.push("adp-day--today");
      if (day.disabled) cls.push("adp-day--disabled");

      if (this.mode === "range") {
        // Bitiş seçilirken hover varsa onu kullan (tek gün start=end olsa bile aralık önizlemesi çalışsın)
        const effectiveEnd = this.selectPhase === "end" && this.hoverDate
          ? this.hoverDate
          : endTs;

        if (startTs !== null && ts === startTs) {
          cls.push("adp-day--start");
          if (effectiveEnd && effectiveEnd > startTs) cls.push("adp-day--start-connected");
        }
        if (effectiveEnd !== null && ts === effectiveEnd && startTs !== null) {
          cls.push("adp-day--end");
          if (startTs < effectiveEnd) cls.push("adp-day--end-connected");
        }
        if (startTs !== null && effectiveEnd !== null && ts > startTs && ts < effectiveEnd) {
          cls.push("adp-day--in-range");
        }
        if (this.selectPhase === "end" && this.hoverDate && startTs !== null && this.hoverDate < startTs) {
          if (ts >= this.hoverDate && ts < startTs) {
            cls.push("adp-day--in-range-preview");
          }
        }
      } else {
        if (this.localValue && ts === this.parseDate(this.localValue).getTime()) {
          cls.push("adp-day--selected");
        }
      }
      return cls;
    },
    clearValue() {
      if (this.mode === "range") {
        this.startDate = null;
        this.endDate = null;
        this.selectPhase = "start";
        this.$emit("input", { start: null, end: null });
      } else {
        this.localValue = null;
        this.$emit("input", null);
      }
      this.activePreset = null;
      this.$emit("clear");
    },
    applyPreset(preset) {
      this.activePreset = preset.key;
      // First navigate to the correct month, then apply dates in next tick
      // to avoid simultaneous re-renders causing visual stutter
      if (preset.start) {
        const d = this.parseDate(preset.start);
        if (d) {
          this.viewYear = d.getFullYear();
          this.viewMonth = d.getMonth();
        }
      }
      this.$nextTick(() => {
        this.startDate = preset.start;
        this.endDate = preset.end;
        this.selectPhase = "start";
        this.$emit("input", { start: preset.start, end: preset.end });
        this.$emit("preset", preset.key);
      });
    },
  },
};
</script>

<style scoped>
.app-date-picker {
  position: relative;
}

.app-date-picker--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.app-date-picker__label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}

.app-date-picker__required {
  color: #e74c3c;
  margin-left: 2px;
}

.app-date-picker__tooltip-icon {
  font-size: 14px;
  color: #999;
  margin-left: 4px;
  cursor: help;
}

/* Input */
.app-date-picker__input {
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.app-date-picker__input:hover {
  border-color: #bbb;
}

.app-date-picker__input--active {
  border-color: #3e416d;
  box-shadow: 0 0 0 2px rgba(62, 65, 109, 0.12);
}

.app-date-picker__icon {
  font-size: 18px;
  color: #999;
  margin-right: 10px;
  flex-shrink: 0;
}

.app-date-picker__value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  flex: 1;
}

.app-date-picker__arrow {
  font-size: 14px;
  color: #bbb;
}

.app-date-picker__placeholder {
  font-size: 14px;
  color: #aaa;
  flex: 1;
}

.app-date-picker__clear {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border: none;
  border-radius: 50%;
  color: #888;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.app-date-picker__clear:hover {
  background: #e74c3c;
  color: #fff;
}

/* Panel */
.app-date-picker__panel {
  position: absolute;
  right: 0;
  display: flex;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.12);
  z-index: 99999;
  overflow: hidden;
}

.app-date-picker__panel--top {
  bottom: calc(100% + 6px);
}

.app-date-picker__panel--bottom {
  top: calc(100% + 6px);
  bottom: auto;
}

/* Sidebar */
.adp-sidebar {
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  min-width: 140px;
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
}

.adp-sidebar__title {
  font-size: 11px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 16px 10px;
}

.adp-sidebar__btn {
  display: block;
  width: 100%;
  padding: 9px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.25s ease, color 0.2s ease;
  white-space: nowrap;
}

.adp-sidebar__btn:hover {
  background: #f0f0f0;
  color: #333;
}

.adp-sidebar__btn--active {
  background: #3e416d;
  color: #fff;
}

.adp-sidebar__btn--active:hover {
  background: #333563;
  color: #fff;
}

/* Body */
.adp-body {
  display: flex;
  flex-direction: column;
}

/* Range bar */
.adp-range-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.adp-range-bar__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 90px;
}

.adp-range-bar__label {
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.adp-range-bar__val {
  font-size: 14px;
  font-weight: 600;
  color: #3e416d;
}

.adp-range-bar__arrow {
  font-size: 14px;
  color: #ccc;
  margin-top: 8px;
}

/* Calendars */
.adp-calendars {
  display: flex;
  padding: 8px 4px 12px;
  gap: 0;
}

.adp-calendars .adp-cal:first-child {
  border-right: 1px solid #e0e0e0;
  padding-right: 8px;
}

.adp-calendars .adp-cal:last-child {
  padding-left: 8px;
}

/* Single Calendar */
.adp-cal {
  width: 280px;
}

.adp-cal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 8px;
  user-select: none;
}

.adp-cal__month {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  text-transform: capitalize;
}

.adp-cal__nav {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #555;
  font-size: 20px;
  transition: all 0.15s ease;
}

.adp-cal__nav:hover {
  background: #f0f0f0;
}

.adp-cal__nav--hidden {
  visibility: hidden;
}

.adp-cal__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.adp-cal__wd {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  padding: 0 0 6px;
  text-transform: uppercase;
}

.adp-cal__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
  min-height: 228px;
}

/* Day cell */
.adp-day {
  position: relative;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background 0.25s ease;
}

.adp-day--empty {
  cursor: default;
}

.adp-day--disabled {
  cursor: default;
  opacity: 0.3;
}

.adp-day--disabled .adp-day__text {
  color: #ccc;
}

.adp-day__text {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #333;
  border-radius: 50%;
  transition: background 0.25s ease, color 0.2s ease;
}

.adp-day:not(.adp-day--disabled):not(.adp-day--empty):not(.adp-day--start):not(.adp-day--end):not(.adp-day--selected):hover .adp-day__text {
  background: #3e416d;
  color: #fff;
}

/* Today */
.adp-day--today .adp-day__text {
  font-weight: 700;
  border: 2px solid #3e416d;
}

/* Selected (single mode) */
.adp-day--selected .adp-day__text {
  background: #3e416d !important;
  color: #fff !important;
  border-color: transparent;
}

/* ===== Range Styles ===== */

/* Start day */
.adp-day--start .adp-day__text {
  background: #3e416d;
  color: #fff;
  border-color: transparent;
}

.adp-day--start-connected {
  background: linear-gradient(to right, transparent 50%, rgba(62, 65, 109, 0.10) 50%);
}

/* End day */
.adp-day--end .adp-day__text {
  background: #3e416d;
  color: #fff;
  border-color: transparent;
}

.adp-day--end-connected {
  background: linear-gradient(to left, transparent 50%, rgba(62, 65, 109, 0.10) 50%);
}

/* In-range */
.adp-day--in-range {
  background: rgba(62, 65, 109, 0.10);
}

.adp-day--in-range .adp-day__text {
  color: #3e416d;
  font-weight: 500;
}

/* Hover preview for in-range */
.adp-day--in-range-preview {
  background: rgba(62, 65, 109, 0.05);
}

.adp-day--in-range-preview .adp-day__text {
  color: #3e416d;
}

/* Transitions */
.app-date-picker-fade-enter-active, .app-date-picker-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.app-date-picker-fade-enter, .app-date-picker-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.app-date-picker__helper {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}
</style>
